import { DEFAULT_BREAKPOINTS } from '../models/project-schema';
import type {
  EditorSettings,
  ResponsiveBreakpoint,
  ResponsiveComponentMetadata,
  ResponsiveIntent,
  ResponsiveIntentKind,
} from '../types';
import { normalizeDesignSystem } from './design-system';

export const INTENT_LABELS: Record<ResponsiveIntentKind, string> = {
  'keep-beside': 'Keep beside',
  'stack-below': 'Stack below',
  'full-width-small': 'Full-width on smaller screens',
  'hide-at-breakpoint': 'Hide at breakpoint',
  'reorder-mobile': 'Reorder on mobile',
  'maintain-aspect': 'Maintain aspect ratio',
  'prioritize-content': 'Prioritize content',
  'mobile-navigation': 'Convert navigation to mobile menu',
  'horizontal-scroll': 'Allow horizontal scrolling',
  accordion: 'Collapse into accordion',
  'responsive-type': 'Use responsive typography',
  'preserve-custom': 'Preserve custom behavior',
};

export const INTENT_PLAIN_EXPLANATIONS: Record<ResponsiveIntentKind, string> = {
  'keep-beside': 'Keeps elements side-by-side in a row without wrapping.',
  'stack-below': 'Stacks elements into a vertical column on smaller screens for easy reading.',
  'full-width-small': 'Expands this element to span the full screen width on smaller devices.',
  'hide-at-breakpoint': 'Hides this element completely on this screen size and smaller.',
  'reorder-mobile': 'Changes visual order on mobile to show key information first.',
  'maintain-aspect': 'Keeps media proportional (e.g. 16:9 or square) to prevent distortion.',
  'prioritize-content': 'Optimizes rendering performance on mobile by prioritizing visible content.',
  'mobile-navigation': 'Transforms navigation links into a compact mobile drawer.',
  'horizontal-scroll': 'Permits smooth horizontal scrolling for wide cards or tables.',
  accordion: 'Collapses long sections into an expandable accordion on small screens.',
  'responsive-type': 'Scales font size fluidly between compact and wide screens.',
  'preserve-custom': 'Preserves custom layout and styling behavior without automatic overrides.',
};

export function normalizeEditorSettings(value?: Partial<EditorSettings>): EditorSettings {
  return {
    mode: value?.mode === 'guided' || value?.mode === 'pro' ? value.mode : 'standard',
    breakpoints: value?.breakpoints?.length ? value.breakpoints : [...DEFAULT_BREAKPOINTS],
    responsiveIntents: value?.responsiveIntents ?? {},
    designSystem: normalizeDesignSystem(value?.designSystem),
    interactions: value?.interactions ?? [],
    ...(value?.menus ? { menus: value.menus } : {}),
  };
}

export function addCustomBreakpoint(
  settings: EditorSettings,
  label: string,
  width: number,
): { settings: EditorSettings; breakpoint: ResponsiveBreakpoint } {
  const normalizedWidth = Math.round(width);
  if (!Number.isFinite(normalizedWidth) || normalizedWidth < 320 || normalizedWidth > 2400)
    throw new Error('Custom breakpoints must be between 320px and 2400px.');
  const existing = settings.breakpoints.find((item) => item.width === normalizedWidth);
  if (existing) return { settings, breakpoint: existing };
  const inheritedFrom = [...settings.breakpoints]
    .filter((item) => item.width > normalizedWidth)
    .sort((a, b) => a.width - b.width)[0]?.id;
  const breakpoint: ResponsiveBreakpoint = {
    id: `custom-${normalizedWidth}`,
    label: label.trim() || `Custom ${normalizedWidth}px`,
    width: normalizedWidth,
    ...(inheritedFrom ? { inheritedFrom } : {}),
  };
  return {
    settings: {
      ...settings,
      breakpoints: [...settings.breakpoints, breakpoint].sort((a, b) => b.width - a.width),
    },
    breakpoint,
  };
}

function media(breakpoint: ResponsiveBreakpoint): string {
  return `(max-width:${breakpoint.width}px)`;
}

export function intentClass(componentId: string): string {
  return `wk-intent-${componentId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

export function responsiveCss(
  componentId: string,
  metadata: ResponsiveComponentMetadata,
  breakpoints: ResponsiveBreakpoint[],
): string {
  const selector = `.${intentClass(componentId)}`;
  const rules: string[] = [];
  for (const intent of metadata.intents.filter((item) => item.enabled)) {
    const breakpoint =
      breakpoints.find((item) => item.id === (intent.breakpoint ?? 'mobile')) ?? breakpoints.at(-1);
    if (!breakpoint) continue;
    const declarations: string[] = [];
    if (intent.kind === 'stack-below') declarations.push('display:flex', 'flex-direction:column');
    if (intent.kind === 'keep-beside')
      declarations.push('display:flex', 'flex-direction:row', 'flex-wrap:nowrap');
    if (intent.kind === 'full-width-small') declarations.push('width:100%', 'max-width:100%');
    if (intent.kind === 'hide-at-breakpoint') declarations.push('display:none');
    if (intent.kind === 'reorder-mobile')
      declarations.push(`order:${Math.max(0, intent.order ?? 1)}`);
    if (intent.kind === 'maintain-aspect')
      declarations.push(`aspect-ratio:${intent.value || '16 / 9'}`, 'object-fit:cover');
    if (intent.kind === 'prioritize-content')
      declarations.push('content-visibility:auto', 'contain-intrinsic-size:320px');
    if (intent.kind === 'horizontal-scroll')
      declarations.push('overflow-x:auto', 'overscroll-behavior-inline:contain');
    if (intent.kind === 'responsive-type') declarations.push('font-size:clamp(1rem, 2.5vw, 2rem)');
    if (intent.kind === 'mobile-navigation')
      declarations.push('display:flex', 'flex-wrap:wrap', 'gap:.75rem');
    if (intent.kind === 'accordion')
      declarations.push('max-height:var(--wk-accordion-height, none)', 'overflow:hidden');
    if (declarations.length)
      rules.push(`@media ${media(breakpoint)}{${selector}{${declarations.join(';')}}}`);
  }
  return rules.join('\n');
}

export function compileProjectResponsiveCss(settings?: EditorSettings): string {
  if (!settings?.responsiveIntents) return '';
  const breakpoints = settings.breakpoints?.length ? settings.breakpoints : [...DEFAULT_BREAKPOINTS];
  return Object.entries(settings.responsiveIntents)
    .map(([id, metadata]) => responsiveCss(id, metadata, breakpoints))
    .filter(Boolean)
    .join('\n');
}

export interface EffectiveIntent extends ResponsiveIntent {
  isOverride: boolean;
  inheritedFrom?: string;
}

export function getEffectiveIntents(
  metadata: ResponsiveComponentMetadata,
  breakpoints: ResponsiveBreakpoint[],
  currentBreakpointId: string,
): EffectiveIntent[] {
  const sorted = [...breakpoints].sort((a, b) => b.width - a.width);
  const current = sorted.find((b) => b.id === currentBreakpointId) ?? sorted.at(-1);
  if (!current) return [];

  const ancestors = sorted.filter((b) => b.width > current.width).reverse();
  const results: EffectiveIntent[] = [];
  const allKinds = Object.keys(INTENT_LABELS) as ResponsiveIntentKind[];

  for (const kind of allKinds) {
    const direct = metadata.intents.find(
      (item) => item.kind === kind && (item.breakpoint ?? 'mobile') === current.id,
    );
    if (direct) {
      results.push({
        ...direct,
        breakpoint: current.id,
        isOverride: Boolean(metadata.overrides?.[current.id]),
      });
      continue;
    }

    let inherited: ResponsiveIntent | undefined;
    let inheritedBreakpoint: ResponsiveBreakpoint | undefined;
    for (const ancestor of ancestors) {
      const match = metadata.intents.find(
        (item) => item.kind === kind && (item.breakpoint ?? 'mobile') === ancestor.id,
      );
      if (match) {
        inherited = match;
        inheritedBreakpoint = ancestor;
        break;
      }
    }

    if (inherited && inherited.enabled && inheritedBreakpoint) {
      results.push({
        ...inherited,
        breakpoint: current.id,
        isOverride: false,
        inheritedFrom: inheritedBreakpoint.label || inheritedBreakpoint.id,
      });
    }
  }

  return results;
}

export function setIntent(
  settings: EditorSettings,
  componentId: string,
  intent: ResponsiveIntent,
): EditorSettings {
  const existing = settings.responsiveIntents[componentId] ?? { intents: [] };
  const targetBreakpoint = intent.breakpoint ?? 'mobile';
  const intents = existing.intents.filter(
    (item) => item.kind !== intent.kind || (item.breakpoint ?? 'mobile') !== targetBreakpoint,
  );
  if (intent.enabled) {
    intents.push({ ...intent, breakpoint: targetBreakpoint });
  }
  const widestBreakpointId =
    [...settings.breakpoints].sort((a, b) => b.width - a.width)[0]?.id ?? 'desktop';
  const isBase = targetBreakpoint === widestBreakpointId;
  const overrides = {
    ...(existing.overrides ?? {}),
    [targetBreakpoint]: !isBase,
  };
  return {
    ...settings,
    responsiveIntents: {
      ...settings.responsiveIntents,
      [componentId]: { ...existing, intents, overrides },
    },
  };
}

export function resetBreakpoint(
  settings: EditorSettings,
  componentId: string,
  breakpoint: string,
): EditorSettings {
  const existing = settings.responsiveIntents[componentId];
  if (!existing) return settings;
  return {
    ...settings,
    responsiveIntents: {
      ...settings.responsiveIntents,
      [componentId]: {
        ...existing,
        intents: existing.intents.filter((item) => (item.breakpoint ?? 'mobile') !== breakpoint),
        overrides: { ...(existing.overrides ?? {}), [breakpoint]: false },
      },
    },
  };
}

export interface ResponsiveWarning {
  kind: 'touch-target' | 'overflow' | 'typography' | 'missing-inheritance';
  message: string;
  suggestion?: string;
}

export function validateResponsive(
  metadata: ResponsiveComponentMetadata,
  component?: {
    width?: number;
    height?: number;
    tagName?: string;
    fontSize?: number;
    isInteractive?: boolean;
  },
  breakpoint?: ResponsiveBreakpoint,
): ResponsiveWarning[] {
  const warnings: ResponsiveWarning[] = [];
  const isCompact = !breakpoint || breakpoint.width <= 768;

  const isInteractive =
    component?.isInteractive ||
    ['a', 'button', 'input', 'select', 'textarea'].includes(
      (component?.tagName ?? '').toLowerCase(),
    ) ||
    metadata.intents.some(
      (item) =>
        (item.kind === 'mobile-navigation' || item.kind === 'prioritize-content') && item.enabled,
    );

  if (isInteractive && isCompact) {
    const w = component?.width ?? 44;
    const h = component?.height ?? 44;
    if (w < 44 || h < 44) {
      warnings.push({
        kind: 'touch-target',
        message: 'Interactive content may be smaller than the 44px touch target.',
        suggestion: 'Increase padding or tap area to satisfy mobile usability standards.',
      });
    }
  }

  if (metadata.intents.some((item) => item.kind === 'horizontal-scroll' && item.enabled)) {
    warnings.push({
      kind: 'overflow',
      message: 'This component intentionally scrolls horizontally on smaller screens.',
      suggestion: 'Ensure visible scroll hints so users discover hidden content.',
    });
  }

  const bpWidth = breakpoint?.width ?? 390;
  if (isCompact && (component?.width ?? 0) > bpWidth) {
    warnings.push({
      kind: 'overflow',
      message: `Element fixed width (${component?.width}px) exceeds screen width (${bpWidth}px).`,
      suggestion: "Apply 'Full-width on smaller screens' or change width to percentage/auto.",
    });
  }

  if (isCompact && (component?.fontSize ?? 0) >= 36) {
    warnings.push({
      kind: 'typography',
      message: 'Fixed large headline may wrap awkwardly on compact mobile screens.',
      suggestion: "Enable 'Use responsive typography' for smooth fluid scaling.",
    });
  }

  if (metadata.intents.some((item) => item.enabled && !item.breakpoint)) {
    warnings.push({
      kind: 'missing-inheritance',
      message: 'This intent inherits its breakpoint from the responsive system.',
    });
  }

  return warnings;
}
